/** @jsx jsx */

import { useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import 'services/ie11closestPolyfill';
import bulletStyles from './bulletStyles';
import { TextToSpeech } from './TextToSpeech';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import { getSCORMInitialized } from 'components/lib/Scorm/SCORM';

const ReadOnlyText = ({
  id,
  content,
  className,
  borderRadius,
  background,
  bulletColor,
  liTextColor,
  linkColors,
  enableTextToSpeechBtn,
  ...props
}) => {
  const textToSpeech = useSelector((state: State) => state.theme.textToSpeech);
  //
  const moduleVariables = useSelector((state: State) => state.moduleVariables);
  const userBehaviour = useSelector((state: State) => state.userBehaviour);

  const readOnlyText = useRef(null);
  const fontInfo = useRef<{ fontSize: number; lineHeight: number } | undefined>(
    undefined
  );

  let padding;

  if (background) {
    padding = `${12.5 + borderRadius}px ${10 + borderRadius}px`;
  }

  if (!!borderRadius) {
    padding = borderRadius * 3;
  }

  useEffect(() => {
    if (!readOnlyText.current) {
      return;
    }

    const varElems = Array.from(
      (readOnlyText.current as HTMLDivElement).querySelectorAll(
        '[data-type="variable"]'
      )
    );

    if (!varElems?.length) {
      return;
    }

    varElems.forEach(vE => {
      const varName = vE.getAttribute('data-varname');
      const { childNodes } = vE;

      if (!varName) {
        return;
      }

      if (varName === 'DATE') {
        vE.textContent = Date()
          .toString()
          .split(/ /g)
          .slice(0, 4)
          .join(' ');
        return;
      }

      if (varName === 'SCORE') {
        //

        const userBehaviourScoreValues =
          Object.values(userBehaviour?.score) || [];
        const maxScore = userBehaviourScoreValues?.length;

        let score = 0;
        let scorePercent = 0;

        if (userBehaviourScoreValues?.length) {
          score = userBehaviourScoreValues.reduce(
            (acc: number, curr: 0 | 1 | 't' | 'f') => {
              let $: 0 | 1 = 0;
              if (typeof curr === 'string' && ['t', 'f'].includes(curr)) {
                $ = curr === 't' ? 1 : 0;
              } else {
                $ = curr as 0 | 1;
              }

              return acc + $;
            },
            0
          );

          scorePercent = Number(((score / maxScore) * 100 || 0).toFixed());
        }

        console.log(`SCORE: ${scorePercent}%`);

        vE.textContent = `${scorePercent}%`;
        //
        return;
      }

      if (childNodes?.length) {
        childNodes[childNodes.length - 1].textContent =
          moduleVariables[varName];
        return;
      }

      vE.textContent = moduleVariables[varName];
    });
  });

  useEffect(() => {
    if (!readOnlyText.current) {
      return;
    }

    const span = (readOnlyText.current as HTMLDivElement).querySelector(
      '[data-type="fontSize"]'
    ) as HTMLSpanElement;

    if (span) {
      fontInfo.current = {
        fontSize: Number(span.style.fontSize.replace(/[^\d.-]+/g, '')),
        lineHeight: Number(span.style.lineHeight.replace(/[^\d.-]+/g, '')),
      };
    }

    const links: HTMLAnchorElement[] = Array.from(
      (readOnlyText.current as HTMLDivElement).querySelectorAll('a')
    );

    links.forEach(link => {
      //
      if (link?.href.includes('mailto:') && getSCORMInitialized()) {
        link.target = '_blank';
      }
    });
    //
  }, []);

  return (
    <div
      ref={readOnlyText}
      className={`chameleon-text${className ? ` ${className}` : ''}`}
      // as="div"
      css={css`
        a:link {
          color: ${linkColors.link} !important;
        }

        a:visited {
          color: ${linkColors.visited} !important;
        }

        a:hover {
          color: ${linkColors.hover} !important;
        }

        a:focus-visible {
          color: ${linkColors.focus} !important;

          outline-offset: 2px !important;
          outline: 1px solid ${linkColors.focus} !important;
          text-decoration: none !important;
        }

        a:active {
          color: ${linkColors.active} !important;
        }

        span[data-type='variable'] {
          white-space: pre-wrap !important;
        }

        ${bulletStyles(bulletColor, liTextColor)}

        ${fontInfo.current &&
          !props.certificate &&
          `
        @media(max-width: ${360 / window.devicePixelRatio}px) {
          span {
            font-size: ${fontInfo.current?.fontSize * 1.6}vw !important;
            line-height: ${fontInfo.current?.lineHeight / 8}vw !important;      
          }
        }
        `}
      `}
      style={
        background
          ? {
              borderRadius,
              background,
              padding: `${12.5 + borderRadius}px ${10 + borderRadius}px`,
            }
          : {}
      }
    >
      <div
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />

      {'speechSynthesis' in window &&
        textToSpeech &&
        enableTextToSpeechBtn &&
        content && (
          <TextToSpeech id={`text-to-speech-${id}`} content={content} />
        )}
    </div>
  );
};

export default ReadOnlyText;
